import React, { useEffect, useRef, useState } from 'react';
import { Card, FormCheck, FormText } from 'react-bootstrap';
import ListGroup from 'react-bootstrap/ListGroup';
import { IMultiSelectOption } from '../../interfaces/IMultiSelectOption';

interface MultiSelectProps {
    name?: string;
    required?: boolean;
    availableOptions?: IMultiSelectOption[] | undefined;
    value?: IMultiSelectOption[] | undefined;
    onChange?: (value: IMultiSelectOption[]) => void;
    showValidation?: boolean;
}

const MultiSelectList: React.FC<MultiSelectProps> = ({
    name,
    required,
    availableOptions,
    value,
    onChange,
    showValidation = false,
}) => {
    const [inputValue, setInputValue] = useState<string>('');
    const [selectedOptions, setSelectedOptions] = useState<IMultiSelectOption[]>(value ?? []);
    const [options, setOptions] = useState<IMultiSelectOption[]>(availableOptions ?? []);
    const listRef = useRef<HTMLDivElement>(null);

    const isInvalid = required && showValidation && selectedOptions.length === 0;

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    const handleOptionClick = (option: IMultiSelectOption) => {
        if (!selectedOptions.find((selectedOption) => selectedOption.id === option.id)) {
            const next = [...selectedOptions, option];
            setSelectedOptions(next);
            if (onChange) onChange(next);
            setInputValue('');
        } else {
            handleRemoveOption(option);
        }
    };

    const handleRemoveOption = (option: IMultiSelectOption) => {
        const updatedOptions = selectedOptions.filter(
            (selectedOption) => selectedOption.id !== option.id
        );
        setSelectedOptions(updatedOptions);
        if (onChange) onChange(updatedOptions);
    };

    useEffect(() => {
        if (availableOptions !== options) setOptions(availableOptions ?? []);
        if (value !== selectedOptions) setSelectedOptions(value ?? []);
    }, [availableOptions, value]);

    return (
        <div>
            <style type="text/css">
                {`
                    .multiselect-list-group {
                        max-height: 100px;
                        overflow-y:auto;
                        text-align:left;
                    }
                `}
            </style>

            {/* Hidden input to integrate with native form validation */}
            {name && (
                <input
                    type="text"
                    name={name}
                    required={required}
                    value={selectedOptions.map((opt) => opt.id).join(',')}
                    onChange={() => { }}
                    style={{ display: 'none' }}
                />
            )}

            <div>
                {isInvalid && (
                    <FormText className="text-danger">This field is required.</FormText>
                )}
                <Card className={`p-2 mb-2`} style={{ borderColor: isInvalid ? '#ff998e' : ''}} >
                    <ListGroup className="multiselect-list-group-noscroll" ref={listRef}>
                        {options
                            .sort((a, b) => {
                                const isOtherA = a.label.startsWith('Other');
                                const isOtherB = b.label.startsWith('Other');
                                if (isOtherA && !isOtherB) return 1;
                                if (!isOtherA && isOtherB) return -1;
                                return a.label.localeCompare(b.label);
                            })
                            .map((option) => (
                                <ListGroup.Item
                                    key={option.id}
                                    onClick={() => handleOptionClick(option)}
                                    style={{ cursor: 'pointer', border: 'none' }}
                                    active={selectedOptions.includes(option)}
                                >
                                    <div style={{ display: 'flex' }}>
                                        <FormCheck
                                            readOnly
                                            checked={selectedOptions.some((x) => x.id === option.id)}
                                            className="me-2"
                                        />
                                        {option.label}
                                    </div>
                                </ListGroup.Item>
                            ))}
                    </ListGroup>
                </Card>


            </div>
        </div>
    );
};

export default MultiSelectList;
